import React from 'react';
import { Link } from 'react-router-dom';
import { Cloud, CheckCircle, ArrowRight, Award, Zap, Shield, TrendingUp, Globe, Server, Database, Lock } from 'lucide-react';


export default function CloudArchitecture() {
  const benefits = [
    {
      icon: <Zap className="w-6 h-6" />,
      title: "High Performance",
      description: "Optimized architectures delivering lightning-fast response times and seamless user experiences"
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Enterprise Security",
      description: "Multi-layered security with encryption, IAM policies, and compliance frameworks"
    },
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: "Auto-Scaling",
      description: "Automatic resource scaling to handle traffic spikes and optimize costs"
    },
    {
      icon: <Globe className="w-6 h-6" />,
      title: "Global Reach",
      description: "Multi-region deployments with CDN integration for worldwide availability"
    }
  ];

  const technologies = [
    { name: "AWS", items: ["EC2", "S3", "RDS", "Lambda", "CloudFront", "Route 53"] },
    { name: "Azure", items: ["Virtual Machines", "Blob Storage", "SQL Database", "Functions", "CDN"] },
    { name: "Infrastructure as Code", items: ["Terraform", "CloudFormation", "ARM Templates", "Pulumi"] },
    { name: "Monitoring", items: ["CloudWatch", "Azure Monitor", "Datadog", "New Relic"] }
  ];

  const process = [
    {
      step: "1",
      title: "Discovery & Assessment",
      description: "Comprehensive audit of your current infrastructure, identifying bottlenecks, security gaps, and optimization opportunities"
    },
    {
      step: "2",
      title: "Architecture Design",
      description: "Custom cloud architecture tailored to your business needs, scalability requirements, and compliance standards"
    },
    {
      step: "3",
      title: "Migration Planning",
      description: "Detailed migration strategy with zero-downtime approach, risk mitigation, and rollback procedures"
    },
    {
      step: "4",
      title: "Implementation",
      description: "Phased migration with continuous testing, performance monitoring, and team training"
    },
    {
      step: "5",
      title: "Optimization",
      description: "Post-migration cost optimization, performance tuning, and ongoing support"
    }
  ];

  const caseStudy = {
    title: "Fintech Platform Migration",
    challenge: "Legacy infrastructure causing 15% monthly downtime and inability to scale during peak trading hours",
    solution: "Migrated to AWS multi-region architecture with auto-scaling EKS clusters and RDS Multi-AZ",
    results: [
      { metric: "Uptime", value: "99.99%", improvement: "+14.99%" },
      { metric: "Response Time", value: "< 100ms", improvement: "-75%" },
      { metric: "Cost", value: "$180K saved", improvement: "-35%" },
      { metric: "Scalability", value: "10x capacity", improvement: "+900%" }
    ]
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center pt-20 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=1920&auto=format&fit=crop&q=80"
            alt="Cloud Architecture"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-purple-900/95 via-indigo-900/90 to-purple-900/95"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 relative z-10">
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2 text-sm text-white mb-6">
              <Cloud className="w-4 h-4 text-cyan-400" />
              <span>Enterprise Cloud Solutions</span>
            </div>

            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Cloud Architecture & Migration
            </h1>

            <p className="text-xl text-gray-200 leading-relaxed mb-8">
              Transform your legacy infrastructure into a modern, scalable cloud architecture.
              From on-premise data centers to cloud-native solutions on AWS and Azure.
            </p>

            <div className="flex flex-wrap gap-4">
              <a href="https://calendly.com/aneeshthedevops/hello-there-intro-call" target="_blank" rel="noopener noreferrer"
                className="bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-gray-900 px-8 py-4 rounded-full font-bold transition-all hover:scale-105 shadow-2xl inline-flex items-center gap-2">
                Schedule Consultation <ArrowRight className="w-5 h-5" />
              </a>
              <Link to="/#case-studies"
                className="bg-white/10 hover:bg-white/20 backdrop-blur-sm border-2 border-white/30 text-white px-8 py-4 rounded-full font-bold transition-all inline-flex items-center gap-2">
                View Case Studies
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Key Benefits */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Why Cloud Architecture Matters</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Modern cloud architecture delivers performance, security, and scalability that on-premise systems can't match
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, idx) => (
              <div key={idx} className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all">
                <div className="w-14 h-14 bg-gradient-to-br from-purple-600 to-indigo-600 rounded-xl flex items-center justify-center text-white mb-4">
                  {benefit.icon}
                </div>
                <h3 className="text-xl font-bold mb-2">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Detailed Service Description */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-20">
            <div>
              <h2 className="text-4xl font-bold mb-6">
                Enterprise-Grade Cloud Solutions
              </h2>
              <p className="text-lg text-gray-700 mb-6">
                I design and implement cloud architectures that power mission-critical applications for fintech, SaaS, and enterprise clients worldwide. With 13+ years of experience across AWS and Azure, I've successfully migrated 50+ applications to the cloud.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-bold mb-1">Multi-Region High Availability</h4>
                    <p className="text-gray-600">Design fault-tolerant systems with automatic failover across geographic regions</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-bold mb-1">Disaster Recovery Planning</h4>
                    <p className="text-gray-600">Comprehensive DR strategies with RTO &lt; 1 hour and RPO &lt; 15 minutes</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-bold mb-1">Cloud Cost Optimization</h4>
                    <p className="text-gray-600">Right-sizing, reserved instances, and automated resource management reducing costs by 40%</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-bold mb-1">Security & Compliance</h4>
                    <p className="text-gray-600">SOC 2, PCI-DSS, HIPAA, and GDPR compliant architectures with zero-trust security</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=800&auto=format&fit=crop&q=80"
                alt="Cloud Infrastructure"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -right-6 bg-white rounded-2xl p-6 shadow-2xl max-w-xs">
                <div className="flex items-center gap-3 mb-2">
                  <Server className="w-8 h-8 text-purple-600" />
                  <div className="text-3xl font-bold text-purple-600">50+</div>
                </div>
                <div className="text-sm text-gray-600">Successful Cloud Migrations</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Technologies */}
      <section className="py-20 bg-gradient-to-br from-purple-50 to-indigo-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Technologies & Tools</h2>
            <p className="text-xl text-gray-600">
              Leveraging best-in-class cloud platforms and infrastructure tools
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {technologies.map((tech, idx) => (
              <div key={idx} className="bg-white rounded-2xl p-6 shadow-lg">
                <h3 className="font-bold text-lg mb-4 text-purple-600">{tech.name}</h3>
                <ul className="space-y-2">
                  {tech.items.map((item, itemIdx) => (
                    <li key={itemIdx} className="flex items-center gap-2 text-gray-700">
                      <div className="w-2 h-2 bg-purple-600 rounded-full"></div>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Migration Process</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A proven 5-step approach to ensure successful cloud migration with minimal risk and zero downtime
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {process.map((item, idx) => (
              <div key={idx} className="relative">
                <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-2xl p-6 hover:shadow-xl transition-all h-full">
                  <div className="w-14 h-14 bg-gradient-to-br from-purple-600 to-indigo-600 rounded-full flex items-center justify-center text-white text-xl font-bold shadow-lg mb-4">
                    {item.step}
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-purple-600">{item.title}</h3>
                  <p className="text-gray-700">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Studies */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Award className="w-16 h-16 mx-auto mb-4 text-purple-600" />
            <h2 className="text-4xl font-bold mb-4">Success Stories</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Real-world cloud migrations delivering measurable business impact
            </p>
          </div>

          <div className="bg-white rounded-3xl p-8 md:p-10 shadow-xl border border-gray-100">
            <h3 className="text-2xl font-bold mb-3 text-purple-600">{caseStudy.title}</h3>

            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div className="bg-red-50 rounded-xl p-5 border-l-4 border-red-500">
                <h4 className="font-bold text-sm mb-2 text-red-900 flex items-center gap-2">
                  <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                  Challenge
                </h4>
                <p className="text-gray-700 text-sm">{caseStudy.challenge}</p>
              </div>
              <div className="bg-green-50 rounded-xl p-5 border-l-4 border-green-500">
                <h4 className="font-bold text-sm mb-2 text-green-900 flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  Solution
                </h4>
                <p className="text-gray-700 text-sm">{caseStudy.solution}</p>
              </div>
            </div>

            <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl p-6">
              <h4 className="font-bold mb-4 text-purple-600">Key Results</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {caseStudy.results.map((result, idx) => (
                  <div key={idx} className="text-center">
                    <div className="text-xs text-gray-600 mb-1">{result.metric}</div>
                    <div className="text-2xl font-bold text-purple-600 mb-1">{result.value}</div>
                    <div className="text-xs text-green-600 font-semibold">{result.improvement}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Migrate to the Cloud?</h2>
          <p className="text-xl text-gray-300 mb-8">
            Let's discuss your cloud architecture needs and create a customized migration plan
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <a href="https://calendly.com/aneeshthedevops/hello-there-intro-call" target="_blank" rel="noopener noreferrer"
              className="bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-gray-900 px-10 py-5 rounded-full font-bold text-lg transition-all hover:scale-105 shadow-2xl inline-flex items-center gap-2">
              Schedule Free Consultation <ArrowRight className="w-6 h-6" />
            </a>
            <Link to="/"
              className="bg-white/10 backdrop-blur-sm border-2 border-white/30 hover:bg-white/20 text-white px-10 py-5 rounded-full font-bold text-lg transition-all inline-flex items-center gap-2">
              Back to Home
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
